import { CheckCircle2Icon, CircleDashedIcon, PencilIcon, PlusIcon, Trash2Icon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useLanguage } from '@/contexts/language-context';

interface Props {
  Logo: React.FC<{ className?: string }>;
  title: string;
  subtitle: string;
  isConfigured: boolean;
  onConfigure: () => void;
  onEdit?: () => void;
  onRemove?: () => void;
}

export function PaymentTile({ Logo, title, subtitle, isConfigured, onConfigure, onEdit, onRemove }: Props) {
  const { t } = useLanguage();

  return (
    <Card className="gap-0 py-0 overflow-hidden">
      <CardHeader className="px-5 pt-5 pb-4 border-b">
        <div className="flex items-start gap-3">
          <Logo className="w-10 h-10 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <CardTitle className="text-sm">{title}</CardTitle>
            <CardDescription className="text-xs mt-0.5">{subtitle}</CardDescription>
          </div>
          {isConfigured ? (
            <span className="flex items-center gap-1 text-xs font-medium text-emerald-600 dark:text-emerald-400 shrink-0">
              <CheckCircle2Icon className="w-3.5 h-3.5" />
              {t.configured}
            </span>
          ) : (
            <span className="flex items-center gap-1 text-xs font-medium text-muted-foreground shrink-0">
              <CircleDashedIcon className="w-3.5 h-3.5" />
              {t.notConfigured}
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent className="px-5 py-3">
        {isConfigured ? (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onEdit} className="gap-1.5">
              <PencilIcon className="w-3 h-3" />
              {t.editConfig}
            </Button>
            {onRemove && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onRemove}
                className="gap-1.5 text-destructive hover:text-destructive"
              >
                <Trash2Icon className="w-3 h-3" />
                {t.removeConfig}
              </Button>
            )}
          </div>
        ) : (
          <Button size="sm" onClick={onConfigure} className="gap-1.5">
            <PlusIcon className="w-3 h-3" />
            {t.configure}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
